import type { CustomThemeDTO } from '@clanfire/shared';
import { cn } from '@/lib/utils';

/**
 * A tiny picture of the app in a theme's own colours (not the current
 * theme's): server list, channel list, chat with a couple of messages, and
 * the member list.
 */
export function ThemeSwatch({ theme, className, detailed }: { theme: CustomThemeDTO; className?: string; detailed?: boolean }) {
  const { background, sidebar, rail, panel, card, text, accent, secondary } = theme.colors;
  const line = (width: string, color = text, opacity = 0.55) => <span className="block h-[4px] rounded-full" style={{ width, background: color, opacity }} />;
  return (
    <span aria-hidden className={cn('flex h-16 w-full overflow-hidden rounded-lg border border-line', className)} style={{ background }}>
      <span className="flex w-[12%] flex-col items-center gap-1 pt-1.5" style={{ background: rail }}>
        {[accent, text, text].map((c, i) => (
          <span key={i} className="block aspect-square w-3/5 rounded-full" style={{ background: c, opacity: i ? 0.35 : 1 }} />
        ))}
      </span>
      <span className="flex w-[24%] flex-col gap-1.5 px-1.5 pt-2" style={{ background: sidebar }}>
        {line('70%')}
        <span className="block h-[7px] rounded-full" style={{ background: accent, opacity: 0.25 }} />
        {line('55%')}
        {detailed && line('65%')}
      </span>
      <span className="flex flex-1 flex-col justify-end gap-1.5 p-1.5">
        <span className="flex items-center gap-1">
          <span className="block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: secondary }} />
          {line('60%', text, 0.8)}
        </span>
        {detailed && (
          <span className="flex items-center gap-1">
            <span className="block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: accent }} />
            {line('45%', text, 0.8)}
          </span>
        )}
        <span className="flex items-center gap-1 rounded px-1 py-[3px]" style={{ background: card }}>
          {line('70%', text, 0.35)}
          <span className="ml-auto block h-[7px] w-3 shrink-0 rounded-full" style={{ background: accent }} />
        </span>
      </span>
      <span className="flex w-[16%] flex-col gap-1.5 px-1 pt-2" style={{ background: panel }}>
        {line('80%', text, 0.4)}
        {line('60%', text, 0.4)}
      </span>
    </span>
  );
}
